import React, { useState, useEffect, useMemo } from 'react';
import { Paper, IconButton, Box, TextField, Autocomplete, InputAdornment, TableRow, TableCell, Chip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import DataTable from '../DataTable';
import * as API from '../../api';
import { formatCurrency } from '../../utils/currencyUtils';
import TourDialog from './TourDialog';

const TOUR_CATEGORIES = [
  { value: 'CRUISE', label: 'Du thuyền' },
  { value: 'DAILY', label: 'Tour ghép hàng ngày' },
  { value: 'PACKAGE', label: 'Tour trọn gói' },
  { value: 'ADVENTURE', label: 'Khám phá - Mạo hiểm' },
  { value: 'ECO_CULTURE', label: 'Sinh thái - Văn hóa' },
  { value: 'FREE_EASY', label: 'Combo (Free & Easy)' },
  { value: 'OTHER', label: 'Khác' },
];

const TourTab = ({ openAddSignal, onCloseAdd }) => {
  const [data, setData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterSupplier, setFilterSupplier] = useState(null);
  const [filterBaseTour, setFilterBaseTour] = useState(null);
  const [filterLocation, setFilterLocation] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);

  const userRole = localStorage.getItem('user_role') || 'SALE';

  const loadData = async () => {
    try {
      const [tourRes, locRes] = await Promise.all([
        API.fetchTours(),
        API.client.get('/locations/')
      ]);
     
      const locMap = {};
      (locRes.data || []).forEach(l => {
        locMap[l.id] = l.name;
      });


      const toursWithLoc = (tourRes.data || []).map(t => ({
        ...t,
        location_display: t.location ? locMap[t.location] : '---'
      }));


      setData(toursWithLoc);
      setLocations(locRes.data || []);
    } catch (e) { console.error(e); }
  };


  useEffect(() => { loadData(); }, []);


  useEffect(() => {
    if (openAddSignal) {
      setEditItem(null);
      setIsOpen(true);
    }
  }, [openAddSignal]);


  const handleDelete = async (code) => {
    if (window.confirm('Xóa tour này sẽ ảnh hưởng các booking cũ. Bạn chắc chắn?')) {
      try {
        await API.deleteTour(code);
        loadData();
      } catch (e) { alert('Không thể xóa tour này.'); }
    }
  };


  const uniqueSuppliers = useMemo(() => {
    const names = data.map(t => t.supplier_name).filter(Boolean);
    return [...new Set(names)].sort();
  }, [data]);


  const uniqueBaseTours = useMemo(() => {
    const bases = data.map(t => t.base_tour_name).filter(Boolean);
    return [...new Set(bases)].sort();
  }, [data]);


  const filteredData = useMemo(() => {
    return data.filter(item => {
      if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !item.tour_code.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (filterSupplier && item.supplier_name !== filterSupplier) return false;
      if (filterBaseTour && item.base_tour_name !== filterBaseTour) return false;
      if (filterLocation && item.location !== filterLocation.id) return false;
      if (filterCategory && item.tour_category !== filterCategory.value) return false;

      return true;
    });
  }, [data, searchTerm, filterSupplier, filterBaseTour, filterLocation, filterCategory]);


  const columns = [
    { label: 'Mã Tour', field: 'tour_code', minWidth: 90 },
    { label: 'Tên Tour', field: 'name', minWidth: 220 },
    { label: 'Loại hình', field: 'tour_category_display', minWidth: 150 },
    { label: 'Địa điểm', field: 'location_display', minWidth: 120 },
    { label: 'Nhóm (Base)', field: 'base_tour_name', minWidth: 140 },
    { label: 'NCC', field: 'supplier_name', minWidth: 140 },
    { label: 'Thời lượng', field: 'duration', minWidth: 90, align: 'center' },
    { label: 'Giá Net', field: 'cost', minWidth: 110, align: 'right' },
    { label: 'Giá Bán', field: 'price', minWidth: 110, align: 'right' },
    { label: 'Lịch trình', field: 'itinerary', minWidth: 100, align: 'center' },
    { label: '', field: 'action', minWidth: 80 }
  ];


  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Tìm tên/mã tour..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
       
        <Autocomplete
          size="small"
          options={TOUR_CATEGORIES}
          getOptionLabel={(opt) => opt.label || ''}
          value={filterCategory}
          onChange={(e, val) => setFilterCategory(val)}
          renderInput={(params) => <TextField {...params} label="Loại hình Tour" />}
          sx={{ width: 180 }}
        />

        <Autocomplete
          size="small"
          options={uniqueSuppliers}
          value={filterSupplier}
          onChange={(e, val) => setFilterSupplier(val)}
          renderInput={(params) => <TextField {...params} label="Nhà cung cấp" />}
          sx={{ width: 180 }}
        />

        <Autocomplete
          size="small"
          options={uniqueBaseTours}
          value={filterBaseTour}
          onChange={(e, val) => setFilterBaseTour(val)}
          renderInput={(params) => <TextField {...params} label="Nhóm Tour (Base)" />}
          sx={{ width: 180 }}
        />

        <Autocomplete
          size="small"
          options={locations}
          getOptionLabel={(opt) => opt.name || ''}
          value={filterLocation}
          onChange={(e, val) => setFilterLocation(val)}
          renderInput={(params) => <TextField {...params} label="Địa điểm" />}
          sx={{ width: 180 }}
        />
      </Box>

      <DataTable
        columns={columns}
        data={filteredData}
        renderRow={(row) => (
          <TableRow key={row.tour_code} hover>
            <TableCell sx={{ fontWeight: 'bold' }}>{row.tour_code}</TableCell>
            <TableCell>{row.name}</TableCell>
            <TableCell>
                <Chip 
                    label={row.tour_category_display || 'Khác'} 
                    size="small" 
                    sx={{ 
                        bgcolor: row.tour_category === 'FREE_EASY' ? '#e3f2fd' : '#f5f5f5',
                        color: row.tour_category === 'FREE_EASY' ? '#1565c0' : '#333',
                        fontWeight: '500'
                    }} 
                />
            </TableCell>
            <TableCell>{row.location_display}</TableCell>
            <TableCell sx={{ color: '#666' }}>{row.base_tour_name}</TableCell>
            <TableCell>{row.supplier_name}</TableCell>
            <TableCell align="center">{row.duration_days} ngày</TableCell>
            <TableCell align="right" sx={{ color: '#d32f2f' }}>
               {formatCurrency(row.cost_price)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
               {formatCurrency(row.base_selling_price)}
            </TableCell>
            {/* THÊM MỚI: Ô HIỂN THỊ ICON XEM LỊCH TRÌNH */}
            <TableCell align="center">
              {row.itinerary ? (
                <IconButton 
                  component="a" 
                  href={row.itinerary} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  color="info" 
                  size="small"
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              ) : (
                <Typography variant="body2" color="text.secondary">---</Typography>
              )}
            </TableCell>
            <TableCell align="center">
              {['MANAGER', 'SOURCING'].includes(userRole) ? (
                <>
                  <IconButton size="small" color="primary" onClick={() => { setEditItem(row); setIsOpen(true); }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(row.tour_code)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
               </>
              ) : (
                <Typography variant="caption" color="text.secondary">Chỉ xem</Typography>
              )}
            </TableCell>
          </TableRow>
        )}
      />

      <TourDialog
        open={isOpen}
        onClose={() => { setIsOpen(false); onCloseAdd(); }}
        initialData={editItem}
        onSuccess={() => { setIsOpen(false); onCloseAdd(); loadData(); }}
      />
    </Paper>
  );
};
export default TourTab;